// selectionCache.js
//
// Family-B cache (see docs/fe-caching-and-formula-performance-design.md §4 Phase 2 / §5) for
// reference-type dropdown / connection-field option lists fetched through `searchEntry`
// (/x/select -> /api/activity/formselection).
//
// Only the stable, form-independent "whole small list" responses are cached; everything else
// (typed search, paged/large lists, filtered/scripted fields, share/runas sessions) bypasses this
// layer untouched. See `isRequestCacheable` / `isResponseCacheable` for the exact gate.
//
// Storage: localStorage, read-through on every open (multi-tab safe — we never trust an in-memory
// mirror). Primary invalidation is the SignalR `selectionInvalidated` push routed in via
// `invalidateModule`; a long TTL is only a backstop for missed pushes (offline / reconnect gaps).

const PREFIX = "sc.";                 // one localStorage entry per cached option list: sc.<key>
const TTL_MS = 30 * 60 * 1000;        // 30 min backstop for missed pushes
const MAX_ENTRIES = 200;             // eviction cap so localStorage can't grow unbounded
const DEFAULT_PAGE_SIZE = 10;

// Timestamp of the last invalidation seen by *this tab*, per invalidation tag. Used to resolve the
// fetch-vs-invalidate race: a response whose request started before the module was invalidated must
// not be written back (it may already be stale). In-memory only — cleared on reload, which is fine
// (a reload re-reads localStorage fresh and any genuinely stale record is dropped on TTL / push).
//
// Keys are namespaced by dimension (see below) so an entry write to module X does not suppress
// caching for dropdowns that merely live on X's form: "s:<id>" = target module, "v:<id>" = form
// module.
const lastInvalidated = Object.create(null);

function now() {
  return new Date().getTime();
}

// A cached option list depends on TWO modules, and either one going stale invalidates it:
//
//   s = target module  (data.connectionId) — the module whose ENTRIES populate the dropdown. Its
//       entry writes change which options exist and what they say.
//   v = form module    (data.vectorId)     — the module that OWNS the connection field. Its design
//       carries the field's Display Information (connectionFieldId / displayTemplate), i.e. WHICH
//       target field is rendered as the option text. Changing it re-renders every option without
//       touching a single target entry (SS05926).
//
// `reference` is a per-row filter *value*, not a module, so it only ever participates in the key.
function sourceModuleIdOf(data) {
  if (!data) return null;
  if (data.connectionId != null && data.connectionId !== 0) return String(data.connectionId);
  return null;
}

function formModuleIdOf(data) {
  if (!data) return null;
  if (data.vectorId != null && data.vectorId !== 0) return String(data.vectorId);
  return null;
}

const TAG_TARGET = "s:";
const TAG_FORM = "v:";

// The form module of a stored record. Older records (written before SS05926) have no `v` field, so
// fall back to the key's vectorId segment — see buildKey.
function formModuleIdOfRecord(record) {
  if (!record) return null;
  if (record.v != null) return String(record.v);
  if (typeof record.k !== "string") return null;
  const parts = record.k.split("|");
  return parts.length > 1 && parts[1] !== "" ? parts[1] : null;
}

// Key identifies one option universe. `reference` distinguishes filtered variants (kept for parity
// with the doc even though cacheable fields — item.x — carry no filter). baseServer scopes by tenant
// so switching servers in the same browser can't cross-read.
function buildKey(data, baseServer) {
  const ref = data && data.reference != null ? data.reference : "";
  return [
    baseServer || "",
    data.vectorId != null ? data.vectorId : "",
    data.activityId != null ? data.activityId : "",
    data.fieldId != null ? data.fieldId : "",
    ref
  ].join("|");
}

function storageKey(key) {
  return PREFIX + key;
}

// Request-side gate (evaluated before the fetch, while `data.x` is still present): only the
// pristine "first page, empty search, no per-id lookup, stable field" request is a cache candidate.
function isRequestCacheable(data) {
  if (!data) return false;
  if (!data.x) return false;                              // server-stamped: field has no filter/script/template
  if (data.id != null) return false;                     // addedNew() single-entry lookup, not a list
  if (data.text != null && data.text !== "") return false;
  if (data.currentPage != null && data.currentPage !== 0) return false;
  return sourceModuleIdOf(data) != null;                 // need a source module to invalidate against
}

// Response-side gate: the response must BE the whole small list (shorter than a page), otherwise it
// is a truncated/paged view and caching it would hide later pages.
function isResponseCacheable(data, responseData) {
  if (!Array.isArray(responseData)) return false;
  const pageSize = data && data.pageSize != null ? data.pageSize : DEFAULT_PAGE_SIZE;
  return responseData.length < pageSize;
}

function readRecord(key) {
  let raw;
  try {
    raw = localStorage.getItem(storageKey(key));
  } catch (err) {
    return null;
  }
  if (!raw) return null;

  let record;
  try {
    record = JSON.parse(raw);
  } catch (err) {
    removeKey(key);
    return null;
  }

  if (!record || (now() - record.t) > TTL_MS) {
    removeKey(key);
    return null;
  }
  return record;
}

function removeKey(key) {
  try {
    localStorage.removeItem(storageKey(key));
  } catch (err) { /* ignore */ }
}

// Enumerate all selection-cache localStorage keys (the bare key, without the sc. prefix).
function allKeys() {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.indexOf(PREFIX) === 0) keys.push(k.slice(PREFIX.length));
    }
  } catch (err) { /* ignore */ }
  return keys;
}

// Bound the store: drop TTL-expired records first, then, if still over the cap, evict oldest-first.
function evictIfNeeded() {
  const keys = allKeys();
  if (keys.length < MAX_ENTRIES) return;

  const records = [];
  keys.forEach((key) => {
    const rec = readRecord(key); // readRecord already drops expired ones as a side effect
    if (rec) records.push({ key, t: rec.t });
  });

  if (records.length < MAX_ENTRIES) return;

  records.sort((a, b) => a.t - b.t); // oldest first
  const overflow = records.length - MAX_ENTRIES + 1; // make room for the incoming write
  for (let i = 0; i < overflow; i++) {
    removeKey(records[i].key);
  }
}

/**
 * Read-through lookup. Returns the cached option array (a fresh copy on every call, since it is
 * re-parsed from the stored JSON — callers mutate option objects, e.g. date formatting in
 * FTSDropdown) or null on miss / expiry.
 */
function getOptions(data, baseServer) {
  if (!isRequestCacheable(data)) return null;
  const record = readRecord(buildKey(data, baseServer));
  return record ? record.d : null;
}

/**
 * Write-through. `requestStartedAt` is the timestamp captured immediately before the network call;
 * if the source module was invalidated after that, the result is discarded (fetch-vs-invalidate
 * race). No-op unless both request and response gates pass.
 */
function setOptions(data, responseData, baseServer, requestStartedAt) {
  if (!isRequestCacheable(data)) return;
  if (!isResponseCacheable(data, responseData)) return;

  const sourceModuleId = sourceModuleIdOf(data);
  const formModuleId = formModuleIdOf(data);

  // Either dimension going stale mid-flight poisons this response: a target-entry write (s) or a
  // form-design save (v). Check both.
  const invAt = Math.max(
    lastInvalidated[TAG_TARGET + sourceModuleId] || 0,
    formModuleId != null ? (lastInvalidated[TAG_FORM + formModuleId] || 0) : 0
  );
  if (invAt > 0 && requestStartedAt != null && invAt >= requestStartedAt) {
    return; // a push landed mid-flight — this list may already be stale, don't cache it
  }

  const key = buildKey(data, baseServer);
  const record = { k: key, s: sourceModuleId, v: formModuleId, d: responseData, t: now() };

  try {
    evictIfNeeded();
    localStorage.setItem(storageKey(key), JSON.stringify(record));
  } catch (err) {
    // Quota or serialization failure: clear the whole cache and give up on this write. Better to
    // lose the cache than to leave a half-written, inconsistent store.
    clearAll();
  }
}

// Drop every stored record matching `predicate(record)`. Unparseable records are dropped too.
function removeWhere(predicate) {
  allKeys().forEach((key) => {
    let raw;
    try {
      raw = localStorage.getItem(storageKey(key));
    } catch (err) {
      return;
    }
    if (!raw) return;
    try {
      const record = JSON.parse(raw);
      if (record && predicate(record)) removeKey(key);
    } catch (err) {
      removeKey(key); // unparseable — drop it
    }
  });
}

/**
 * SignalR `selectionInvalidated` handler (entry write/delete): drop every record whose OPTIONS come
 * from `moduleId`, and record the invalidation timestamp so an in-flight fetch for the same module
 * won't write a stale result back.
 */
function invalidateModule(moduleId) {
  if (moduleId == null) return;
  const id = String(moduleId);
  lastInvalidated[TAG_TARGET + id] = now();

  removeWhere((record) => String(record.s) === id);
}

/**
 * SignalR `workspaceChanged` handler (module DESIGN save): a design change to `moduleId` can restyle
 * option text without any entry write, so `invalidateModule` alone never fires. Two ways it lands:
 *
 *   - as the FORM module: its connection field's Display Information (connectionFieldId /
 *     displayTemplate) chose a different target field to render — SS05926.
 *   - as the TARGET module: a renamed/retyped field changes what the chosen display field renders as.
 *
 * Both are cheap to drop and rare (design saves are not a hot path), so invalidate both dimensions.
 */
function invalidateDesign(moduleId) {
  if (moduleId == null) return;
  const id = String(moduleId);
  const at = now();
  lastInvalidated[TAG_TARGET + id] = at;
  lastInvalidated[TAG_FORM + id] = at;

  removeWhere((record) =>
    String(record.s) === id || formModuleIdOfRecord(record) === id
  );
}

/**
 * Drop the entire selection cache. Called on SignalR reconnect (updates during the drop were
 * missed, so the whole cache is suspect) and on quota failure.
 */
function clearAll() {
  allKeys().forEach(removeKey);
}

export default {
  getOptions,
  setOptions,
  invalidateModule,
  invalidateDesign,
  clearAll,
  // exposed for unit tests
  buildKey,
  isRequestCacheable,
  isResponseCacheable,
  sourceModuleIdOf,
  formModuleIdOf
};
